import React, { useState } from "react";
import { handleKeyboardEventBehavior } from "@qlik/qlik/src/lib/util/helpers";
import * as styles from "./styles.module.css";
import { useI18n } from "@qlik/utils/i18nContext";
import { useLocation } from "@reach/router";
import { usePageList } from "@qlik/utils/pageListContext";

// React hook to get data structure necessary for constructing a language switcher.
export function useLanguageSwitcher(i18n) {
    // Grab the current location and page list in order to determine whether the current path exists in the destination language.
    const location = useLocation();
    const { pageList } = usePageList();

    // Get the unprefixed path.
    const currentPath = location.pathname;
    const unPrefixedPath = i18n.getUnPrefixedPath(currentPath);

    // For each language...
    return Object.values(i18n.map).map((lang) => {
        // Determine the preferred destination (the current path but prefixed with the destination language).
        const preferredDestinationPath = i18n.getLangPrefixedPath(unPrefixedPath, lang.contentfulCode);

        // If the preferred destination is in the list of existing pages, go there, otherwise go to the language home page.
        lang.destination = pageList?.[preferredDestinationPath] ? preferredDestinationPath : lang.pathPrefix;

        // Set the active flag if the current language (in the loop) matches the site current language.
        lang.isActive = i18n.curr.langCode === lang.langCode;
        return lang;
    });
}

// Builds the list links for navigating between multilingual sites.
const LanguageOptions = () => {
    const { i18n } = useI18n();

    // Define the languages for the language switcher
    const languages = useLanguageSwitcher(i18n);

    return (
        <div className={`absolute w-full bg-white border border-stone rounded-xs`}>
            <ul className="p-4 max-xl:p-5 flex flex-col max-xl:gap-6 xl:gap-3.75 xl:shadow-xlSmall">
                {languages.map((language) => (
                    <li key={language.langCode} data-lang-key={language.langCode}>
                        <a
                            href={language.destination}
                            target="_self"
                            className={`z-1 group/link relative flex flex-col justify-center gap-1.5 h-full pl-5.5 text-gray tracking-normal leading-tight xl:text-sm transition-colors duration-300 xl:pl-4.25 before:content-[''] before:absolute before:h-full before:w-0.5 before:left-0 before:t-0 before:transition-colors before:duration-300 xl:hover:before:bg-green xl:hover:text-green ${
                                language.isActive ? "before:bg-green text-green" : "before:bg-stone/50"
                            }`}
                            tabIndex="-1"
                        >
                            <span className="-z-1 absolute -inset-2.5 rounded bg-white shadow-xlSmall opacity-0 transition-opacity transition-300 xl:group-hover/link:opacity-100"></span>
                            {language.langName}
                        </a>
                    </li>
                ))}
            </ul>
        </div>
    );
};

const LanguageSelector = () => {
    const [isActive, setIsActive] = useState(false);

    const handleLanguageDropdown = () => {
        setIsActive(!isActive);
    };

    return (
        <>
            <button
                aria-label="Change Language"
                className={`bg-globe cursor-pointer xl:hover:opacity-80 ml-auto mr-5 w-5.5 h-5.5 hidden xl:block`}
                tabIndex="0"
                onKeyDown={(e) => {
                    handleKeyboardEventBehavior(e, handleLanguageDropdown);
                }}
                onClick={handleLanguageDropdown}
            />
            <div className={`absolute min-w-50 bottom-0 min-[1200px]:-ml-36 min-[1330px]:-ml-22`}>
                <div className={`relative cursor-pointer bg-white`}>{isActive && <LanguageOptions />}</div>
            </div>
        </>
    );
};

export default LanguageSelector;
